/**
 * Two-step game selector for scorecard upload.
 *
 * Step 1: Select a date from the calendar
 * Step 2: Select a game from the dropdown
 */
import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, isSameDay, isBefore, isAfter, parseISO } from 'date-fns';
import { DayPicker, CalendarDay, type Modifiers } from 'react-day-picker';
import type { ButtonHTMLAttributes } from 'react';
import { mlbApi } from '../../services/api';
import type { CalendarMetadataResponse, ScheduleGame } from '../../types/gameday';
import { cn } from '../../lib/utils';
import { STALE_TIMES } from '../../types/statcast';
import { useGamedaySchedule } from '../../hooks/useGamedaySchedule';

interface GameSelectorProps {
  /** Currently selected game ID */
  selectedGamePk: number | null;
  /** Callback when a game is selected */
  onGameSelect: (gamePk: number | null) => void;
}

export function GameSelector({ selectedGamePk, onGameSelect }: GameSelectorProps) {
  // Internal state for date selection
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [displayMonth, setDisplayMonth] = useState(new Date());

  // Fetch calendar metadata for game counts and date bounds (completed games only for upload)
  const { data: metadata, isLoading: metadataLoading } = useQuery<CalendarMetadataResponse>({
    queryKey: ['calendar-metadata', 'completed-only'],
    queryFn: () => mlbApi.getCalendarMetadata({ completedOnly: true }),
    staleTime: STALE_TIMES.CAREER_STATS, // 30 minutes
  });

  // Fetch games for selected date, then filter to completed games only
  const dateString = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null;
  const { games: allGames, isLoading: gamesLoading } = useGamedaySchedule({ date: dateString });
  const games = useMemo(() => allGames.filter((g) => g.status.code === 'F'), [allGames]);

  // Clear game selection when date changes
  useEffect(() => {
    onGameSelect(null);
  }, [selectedDate, onGameSelect]);

  // Parse min/max dates from metadata
  const bounds = useMemo(() => {
    if (!metadata?.min_date || !metadata?.max_date) {
      return { min: null, max: null };
    }
    return {
      min: parseISO(metadata.min_date),
      max: parseISO(metadata.max_date),
    };
  }, [metadata]);

  // Clamp displayMonth to bounds once metadata loads (e.g. today is Feb 2026
  // but last completed game is Oct 2025 — start on the most recent month)
  useEffect(() => {
    if (bounds.max && isAfter(displayMonth, bounds.max)) {
      setDisplayMonth(new Date(bounds.max.getFullYear(), bounds.max.getMonth(), 1));
    }
  }, [bounds.max]);

  // Check if a date has games
  const hasGames = (date: Date): boolean => {
    if (!metadata?.game_counts) return false;
    const dateStr = format(date, 'yyyy-MM-dd');
    return (metadata.game_counts[dateStr] ?? 0) > 0;
  };

  // Get game count for a date
  const getGameCount = (date: Date): number => {
    if (!metadata?.game_counts) return 0;
    const dateStr = format(date, 'yyyy-MM-dd');
    return metadata.game_counts[dateStr] ?? 0;
  };

  // Check if a date is within bounds
  const isWithinBounds = (date: Date): boolean => {
    if (!bounds.min || !bounds.max) return true;
    return !isBefore(date, bounds.min) && !isAfter(date, bounds.max);
  };

  const today = new Date();

  const handleDateSelect = (date: Date | undefined) => {
    if (date && hasGames(date) && isWithinBounds(date)) {
      setSelectedDate(date);
    }
  };

  const handleGameChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onGameSelect(value ? parseInt(value, 10) : null);
  };

  // Format game for dropdown display
  const formatGameOption = (game: ScheduleGame): string => {
    return `${game.away_team.name} at ${game.home_team.name} - ${game.start_time_display}`;
  };

  // Custom day cell component to show game counts
  interface DayButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    day: CalendarDay;
    modifiers: Modifiers;
  }

  const DayWithCount = ({ day, modifiers, ...props }: DayButtonProps) => {
    const gameCount = getGameCount(day.date);
    const hasGamesOnDay = gameCount > 0;
    const withinBounds = isWithinBounds(day.date);
    const isDisabled = !hasGamesOnDay || !withinBounds || modifiers.outside;
    const isSelected = selectedDate && isSameDay(day.date, selectedDate);
    const isTodayDate = isSameDay(day.date, today);

    return (
      <button
        {...props}
        disabled={isDisabled}
        className={cn(
          'relative flex h-10 w-9 flex-col items-center justify-start rounded-md p-1 text-sm transition-colors',
          // Base styles
          'hover:bg-accent hover:text-accent-foreground',
          // Disabled state
          isDisabled && 'pointer-events-none opacity-30',
          // Selected state
          isSelected && 'bg-primary text-primary-foreground hover:bg-primary/90',
          // Today highlight (when not selected)
          isTodayDate && !isSelected && 'bg-accent text-accent-foreground ring-1 ring-primary',
          // Outside month
          modifiers.outside && 'text-muted-foreground opacity-50'
        )}
      >
        <span className="font-medium text-xs">{day.date.getDate()}</span>
        {hasGamesOnDay && !modifiers.outside && (
          <span
            className={cn(
              'text-[9px] leading-none',
              isSelected ? 'text-primary-foreground/80' : 'text-muted-foreground'
            )}
          >
            {gameCount}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="space-y-4">
      {/* Step 1: Date Picker */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          1. Select Date <span className="text-red-500">*</span>
        </label>

        {metadataLoading ? (
          <div className="flex items-center justify-center p-8 border rounded-lg">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="border rounded-lg p-3 bg-gray-50 dark:bg-gray-800">
            {/* Month/Year selectors */}
            <div className="flex items-center justify-center gap-2 mb-3">
              <select
                value={displayMonth.getMonth()}
                onChange={(e) =>
                  setDisplayMonth(new Date(displayMonth.getFullYear(), parseInt(e.target.value), 1))
                }
                className="text-sm font-semibold bg-transparent border border-border rounded px-2 py-1 cursor-pointer"
                aria-label="Select month"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i} value={i}>
                    {format(new Date(2000, i, 1), 'MMMM')}
                  </option>
                ))}
              </select>
              <select
                value={displayMonth.getFullYear()}
                onChange={(e) =>
                  setDisplayMonth(new Date(parseInt(e.target.value), displayMonth.getMonth(), 1))
                }
                className="text-sm font-semibold bg-transparent border border-border rounded px-2 py-1 cursor-pointer"
                aria-label="Select year"
              >
                {(() => {
                  const minYear = bounds.min ? bounds.min.getFullYear() : 1900;
                  const maxYear = bounds.max ? bounds.max.getFullYear() : new Date().getFullYear();
                  return Array.from({ length: maxYear - minYear + 1 }, (_, i) => {
                    const y = maxYear - i;
                    return (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    );
                  });
                })()}
              </select>
            </div>

            {/* Calendar Grid */}
            <DayPicker
              mode="single"
              selected={selectedDate ?? undefined}
              onSelect={handleDateSelect}
              month={displayMonth}
              showOutsideDays={true}
              fixedWeeks
              disabled={(date) => !hasGames(date) || !isWithinBounds(date)}
              components={{
                DayButton: DayWithCount,
              }}
              classNames={{
                months: 'flex flex-col',
                month: 'space-y-1',
                month_caption: 'hidden',
                nav: 'hidden',
                month_grid: 'w-full border-collapse',
                weekdays: 'flex',
                weekday:
                  'text-muted-foreground rounded-md w-9 font-normal text-[0.7rem] flex-1 text-center',
                week: 'flex w-full mt-1',
                day: 'flex-1 text-center p-0 relative',
                day_button: 'p-0 font-normal',
              }}
            />

            {/* Selected date display */}
            {selectedDate && (
              <div className="mt-2 pt-2 border-t text-sm text-center text-gray-600 dark:text-gray-400">
                Selected: {format(selectedDate, 'EEEE, MMMM d, yyyy')}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Step 2: Game Dropdown */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          2. Select Game <span className="text-red-500">*</span>
        </label>
        <select
          value={selectedGamePk ?? ''}
          onChange={handleGameChange}
          disabled={!selectedDate || gamesLoading}
          className={cn(
            'w-full px-3 py-2 border border-gray-300 rounded-md',
            'focus:outline-none focus:ring-2 focus:ring-blue-500',
            'dark:bg-gray-700 dark:border-gray-600 dark:text-white',
            'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-800'
          )}
        >
          {!selectedDate ? (
            <option value="">Select a date first</option>
          ) : gamesLoading ? (
            <option value="">Loading games...</option>
          ) : games.length === 0 ? (
            <option value="">No games on this date</option>
          ) : (
            <>
              <option value="">Select a game</option>
              {games.map((game) => (
                <option key={game.game_pk} value={game.game_pk}>
                  {formatGameOption(game)}
                </option>
              ))}
            </>
          )}
        </select>
      </div>
    </div>
  );
}

export default GameSelector;
